import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    TextField,
    ToggleButton,
    ToggleButtonGroup
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import dayjs from "dayjs";
import { DayCalendarSkeleton, PickersDay } from "@mui/x-date-pickers";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import Typography from "@mui/material/Typography";
import './activities.css';
import { Stack } from "@mui/system";
import { daysOfWeekMap } from "../../Helpers/DatesAndTimes";

const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function ActivityDialogEditDates({ isOpen, onClose, eventActivity, setEventActivity }) {

    const { PA_GlobalObject } = usePA_GlobalStore();

    const [eventHasDates] = useState(() => usePA_GlobalStore.getState().PA_GlobalObject.Planning.time_table?.dates?.length > 0);
    const [eventHasDays] = useState(() => usePA_GlobalStore.getState().PA_GlobalObject.Planning.time_table?.days?.length > 0);

    const [activityDaysCount, setActivityDaysCount] = useState(
        eventActivity["time_table"].dates[0] ?
            eventActivity["time_table"].dates.length - 1
            :
            eventActivity["time_table"].days[0] ?
                eventActivity["time_table"].days.length - 1
                :
                0
    );

    const [activityStartingOnDay, setActivityStartingOnDay] = useState(
        eventActivity["time_table"].dates[0] ?
            dayjs(eventActivity["time_table"].dates[0].date).format('dddd')
            :
            eventActivity["time_table"].days[0] ?
                Object.keys(eventActivity["time_table"].days[0]).find(k => k.startsWith('day '))
                :
                ""
    );

    const [tabValueSchedule, setTabValueSchedule] = useState(
        eventActivity.time_table.dates.length > 0 || !eventActivity.time_table.days.length
            ? "date"
            : "day"
    );

    const handleTabValueScheduleChange = (event, newValue) => {
        setTabValueSchedule(newValue);
    };

    const [selectedActivityDates, setSelectedActivityDates] = useState(
        eventActivity["time_table"].dates.length > 0 ? eventActivity["time_table"].dates : []
    );

    const [selectedActivityDays, setSelectedActivityDays] = useState(
        eventActivity["time_table"].days.length > 0 ? eventActivity["time_table"].days : []
    );

    const handleBeginTimeChange = (newTime) => {
        setSelectedActivityDates(prevDates =>
            prevDates.map((entry, index) => {
                if (timeMode === 'multi-day') {
                    if (index === 0) return { ...entry, begin_time: dayjs(newTime).format('h:mm A') };
                    if (index === prevDates.length - 1) return { ...entry };
                    return { ...entry, "all-day": true };
                }
                if (timeMode === 'all-day') {
                    return { ...entry, "all-day": false, begin_time: dayjs(newTime).format('h:mm A') };
                }
                return entry;
            })
        );

        setSelectedActivityDays(prevDays =>
            prevDays.map((entry, index) => {
                if (timeMode === 'multi-day') {
                    if (index === 0) return { ...entry, begin_time: dayjs(newTime).format('h:mm A') };
                    if (index === prevDays.length - 1) return { ...entry };
                    return { ...entry, "all-day": true };
                }
                if (timeMode === 'all-day') {
                    return { ...entry, "all-day": false, begin_time: dayjs(newTime).format('h:mm A') };
                }
                return entry;
            })
        );
    };

    const handleEndTimeChange = (newTime) => {
        setSelectedActivityDates(prevDates =>
            prevDates.map((entry, index) => {
                if (timeMode === 'multi-day') {
                    if (index === prevDates.length - 1) return { ...entry, end_time: dayjs(newTime).format('h:mm A') };
                    return entry;
                }
                if (timeMode === 'all-day') {
                    return { ...entry, "all-day": false, end_time: dayjs(newTime).format('h:mm A') };
                }
                return entry;
            })
        );

        setSelectedActivityDays(prevDays =>
            prevDays.map((entry, index) => {
                if (timeMode === 'multi-day') {
                    if (index === prevDays.length - 1) return { ...entry, end_time: dayjs(newTime).format('h:mm A') };
                    return entry;
                }
                if (timeMode === 'all-day') {
                    return { ...entry, "all-day": false, end_time: dayjs(newTime).format('h:mm A') };
                }
                return entry;
            })
        );
    };

    const [selectTimeToggle, setSelectTimeToggle] = useState(() => {
        const dates = eventActivity?.time_table?.dates || eventActivity?.time_table?.days || [];
        return dates[0]?.begin_time || false;
    });

    const rawBeginTime = eventActivity['time_table'].dates[0]?.begin_time || eventActivity['time_table'].days[0]?.begin_time || null;
    const rawEndTime = eventActivity['time_table'].dates[eventActivity['time_table'].dates.length - 1]?.end_time || eventActivity['time_table'].days[eventActivity['time_table'].days.length - 1]?.end_time || null;

    const [selectedActivityBeginTime, setSelectedActivityBeginTime] = useState(rawBeginTime ? dayjs(rawBeginTime, 'h:mm A') : null);
    const [selectedActivityEndTime, setSelectedActivityEndTime] = useState(rawEndTime ? dayjs(rawEndTime, 'h:mm A') : null);

    const handleActivityDaysChange = (dayIndex, dayName) => {
        setSelectedActivityDays(prev => {
            const exists = prev.some(day => {
                const key = Object.keys(day).find(k => k.startsWith('day '));
                return key === dayIndex;
            });
            let updated;

            if (exists) {
                updated = prev.filter(day => {
                    const key = Object.keys(day).find(k => k.startsWith('day '));
                    return key !== dayIndex;
                });
            } else {
                updated = [...prev, { [dayIndex]: dayName, "all-day": true, begin_time: null, end_time: null, notes: "" }];
            }

            const sorted = [...updated].sort((a, b) => {
                const aKey = Object.keys(a).find(k => k.startsWith("day "));
                const bKey = Object.keys(b).find(k => k.startsWith("day "));
                const aIndex = aKey ? parseInt(aKey.replace('day ', ''), 10) : 0;
                const bIndex = bKey ? parseInt(bKey.replace('day ', ''), 10) : 0;
                return aIndex - bIndex;
            });

            if (timeMode === 'multi-day') {
                if (sorted.length < 2 || !areDayIndexesConsecutive(sorted)) {
                    setTimeMode('all-day');
                    return sorted;
                }

                const beginTime = selectedActivityBeginTime?.format('h:mm A') || null;
                const endTime = selectedActivityEndTime?.format('h:mm A') || null;

                return sorted.map((entry, index) => {
                    if (index === 0) return { ...entry, "all-day": false, begin_time: beginTime, end_time: null };
                    if (index === sorted.length - 1) return { ...entry, "all-day": false, begin_time: null, end_time: endTime };
                    return { ...entry, "all-day": true, begin_time: null, end_time: null };
                });
            }
            return sorted;
        });
    };

    const [calendarDate, setCalendarDate] = useState(() =>
        eventActivity['time_table'].dates.length > 0 ? dayjs(eventActivity['time_table'].dates[0].date, "YYYY-MM-DD") : dayjs()
    );

    const areDatesConsecutive = (dates) => {
        const sorted = [...dates].map(d => dayjs(d.date)).sort((a, b) => a.unix() - b.unix());
        for (let i = 1; i < sorted.length; i++) {
            if (!sorted[i - 1].add(1, 'day').isSame(sorted[i], 'day')) return false;
        }
        return true;
    };

    function isHighlighted(date) {
        return selectedActivityDates.some(dateEntry => dateEntry.date === date.format('YYYY-MM-DD'));
    }

    function ServerDay(props) {
        const { day, outsideCurrentMonth, planningData, ...other } = props;
        const isSelected = isHighlighted(dayjs(day));
        const eventDates = planningData?.time_table?.dates?.map(d => dayjs(d.date, "YYYY-MM-DD")) || [];
        const isEventDate = eventDates.some(eventDay => eventDay.isSame(day, "day"));

        return (
            <PickersDay
                {...other}
                outsideCurrentMonth={outsideCurrentMonth}
                day={day}
                onClick={() => handleDateClick(day)}
                sx={{
                    transition: 'all 0.2s ease-in-out',
                    ...(isSelected
                        ? {
                            backgroundColor: "#f09637 !important",
                            color: "white !important",
                            borderRadius: "50%",
                            boxShadow: '0 4px 10px rgba(240, 150, 55, 0.4)',
                        }
                        : {
                            backgroundColor: "transparent",
                            color: outsideCurrentMonth ? "#ccc" : "text.primary",
                            border: dayjs().isSame(dayjs(day), 'day') ? '1px solid #f09637' : 'none',
                            '&:hover': { backgroundColor: 'rgba(240, 150, 55, 0.08)' }
                        }),
                    position: "relative",
                    borderBottom: isEventDate && !isSelected ? "3px solid #f09637" : "none",
                }}
            >
                <span>{day.format("D")}</span>
            </PickersDay>
        );
    }

    const handleDateClick = (_date) => {
        const formattedDate = _date.format('YYYY-MM-DD');

        setSelectedActivityDates((prev) => {
            const exists = prev.some(entry => entry.date === formattedDate);
            let updated;

            if (exists) {
                updated = prev.filter(entry => entry.date !== formattedDate);
            } else {
                updated = [...prev, { date: formattedDate, "all-day": true, begin_time: null, end_time: null, notes: "" }];
            }

            setSelectTimeToggle(updated.length > 1);
            const sorted = updated.sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix());

            if (timeMode === 'multi-day') {
                if (sorted.length < 2 || !areDatesConsecutive(sorted)) {
                    setTimeMode('all-day');
                    return sorted;
                }

                const begin_time = selectedActivityBeginTime?.format('h:mm A') || null;
                const end_time = selectedActivityEndTime?.format('h:mm A') || null;

                return sorted.map((entry, index) => {
                    if (index === 0) return { ...entry, "all-day": false, begin_time, end_time: null };
                    if (index === sorted.length - 1) return { ...entry, "all-day": false, begin_time: null, end_time };
                    return { ...entry, "all-day": true, begin_time: null, end_time: null };
                });
            }
            return sorted;
        });
    };

    const renderActivityDaysLayoutForBegin = () => {
        const planningDays = usePA_GlobalStore.getState().PA_GlobalObject.Planning.time_table?.days || [];

        if (planningDays.length === 0) {
            return (
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: '6px', pb: 2 }}>
                    {daysOfWeek.map((day, index) => (
                        <Box key={index} sx={{ flex: 1, aspectRatio: '1 / 1', maxWidth: 'calc((100% - 6 * 6px) / 7)' }}>
                            <Chip
                                label={<Typography variant="body2">{day.slice(0, 2)}</Typography>}
                                sx={{
                                    width: '100%', height: '100%',
                                    backgroundColor: 'rgba(240, 150, 55, 0.08)',
                                    borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center',
                                    '&:hover': { backgroundColor: 'rgba(240, 150, 55, 0.16)' }
                                }}
                                clickable
                                onClick={() => handleActivityDaysChange(`day ${index + 1}`, day)}
                            />
                        </Box>
                    ))}
                </Box>
            );
        }

        const calendarDays = planningDays.map((dayObj, index) => {
            const dayKey = Object.keys(dayObj).find(k => k.startsWith('day '));
            const dayName = dayObj[dayKey];
            return { key: dayKey, name: dayName, index: index + 1 };
        });

        const weeks = [];
        let currentWeek = Array(7).fill(null);

        calendarDays.forEach((dayObj) => {
            const dayOfWeekIndex = daysOfWeek.indexOf(dayObj.name);
            if (dayOfWeekIndex !== -1) {
                currentWeek[dayOfWeekIndex] = dayObj;
                if (dayOfWeekIndex === 6) {
                    weeks.push(currentWeek);
                    currentWeek = Array(7).fill(null);
                }
            }
        });
        if (currentWeek.some(day => day !== null)) weeks.push(currentWeek);

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', pb: 2, width: '100%' }}>
                {weeks.map((week, weekIndex) => (
                    <Box key={weekIndex} sx={{ display: 'flex', justifyContent: 'center', gap: '6px', width: '100%' }}>
                        {week.map((day, dayIndex) => {
                            const isSelected = day && selectedActivityDays.some(d => Object.keys(d).find(k => k.startsWith('day ')) === day.key);
                            return (
                                <Box key={dayIndex} sx={{ flex: '1 1 calc(100% / 7)', maxWidth: '45px', aspectRatio: '1', display: 'flex', justifyContent: 'center' }}>
                                    {day ? (
                                        <Chip
                                            label={<Typography variant="body2" sx={{ fontWeight: isSelected ? '700' : '500' }}>{day.name.slice(0, 2)}</Typography>}
                                            sx={{
                                                width: '100%', height: '100%',
                                                backgroundColor: isSelected ? '#f09637 !important' : 'rgba(240, 150, 55, 0.08) !important',
                                                color: isSelected ? '#fff !important' : 'text.primary',
                                                borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center',
                                                boxShadow: isSelected ? '0 4px 8px rgba(240, 150, 55, 0.3)' : 'none',
                                                transition: 'all 0.2s',
                                                '&:hover': { backgroundColor: isSelected ? '#e08526 !important' : 'rgba(240, 150, 55, 0.16) !important' }
                                            }}
                                            clickable
                                            onClick={() => handleActivityDaysChange(day.key, day.name)}
                                        />
                                    ) : (
                                        <Box sx={{ width: '100%', height: '100%', border: '1px dashed #e0e0e0', borderRadius: '12px' }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                ))}
            </Box>
        );
    };

    function areDayIndexesConsecutive(days) {
        const indexes = days
            .map((d) => {
                const dayKey = Object.keys(d).find(key => key.startsWith('day '));
                if (!dayKey) return null;
                return parseInt(dayKey.replace('day ', ''), 10);
            })
            .filter((val) => val !== null)
            .sort((a, b) => a - b);

        if (indexes.length < 2) return false;
        return indexes.every((val, i, arr) => i === 0 || val === arr[i - 1] + 1);
    }

    const handleActivityDaysCountChange = (event) => {
        const newDaysCount = parseInt(event.target.value, 10);
        setActivityDaysCount(newDaysCount);

        if (isNaN(newDaysCount) || newDaysCount <= 0) return;

        const startDayIndex = daysOfWeekMap[activityStartingOnDay] ?? NaN;
        if (isNaN(startDayIndex)) return;

        const newSelectedDays = [];

        for (let i = 0; i < newDaysCount; i++) {
            const dayIndex = (startDayIndex + i) % 7;
            const dayName = dayjs().day(dayIndex).format('dddd');

            let isAllDay = true;
            let begin_time = null;
            let end_time = null;

            const hasValidBegin = selectedActivityBeginTime && dayjs(selectedActivityBeginTime).isValid();
            const hasValidEnd = selectedActivityEndTime && dayjs(selectedActivityEndTime).isValid();

            if (timeMode === 'multi-day') {
                if (i === 0) {
                    isAllDay = false;
                    begin_time = hasValidBegin ? dayjs(selectedActivityBeginTime).format('h:mm A') : null;
                } else if (i === newDaysCount - 1) {
                    isAllDay = false;
                    end_time = hasValidEnd ? dayjs(selectedActivityEndTime).format('h:mm A') : null;
                } else {
                    isAllDay = true;
                }
            }

            newSelectedDays.push({
                [`day ${i + 1}`]: dayName,
                'all-day': isAllDay,
                'begin_time': begin_time,
                'end_time': end_time,
                'notes': "",
            });
        }
        setSelectedActivityDays(newSelectedDays);
    };

    function handleActivityStartingOnChange(event) {
        const newStartingDay = event.target.value;
        const newSelectedDays = [];

        const startDayIndex = daysOfWeekMap[newStartingDay] ?? NaN;
        if (isNaN(startDayIndex)) return;

        const hasValidBegin = selectedActivityBeginTime && dayjs(selectedActivityBeginTime).isValid();
        const hasValidEnd = selectedActivityEndTime && dayjs(selectedActivityEndTime).isValid();

        for (let i = 0; i < activityDaysCount; i++) {
            const dayIndex = (startDayIndex + i) % 7;
            const dayName = dayjs().day(dayIndex).format('dddd');

            let isAllDay = true;
            let begin_time = null;
            let end_time = null;

            if (timeMode === 'per-day') {
                isAllDay = false;
                begin_time = hasValidBegin ? dayjs(selectedActivityBeginTime).format('h:mm A') : null;
                end_time = hasValidEnd ? dayjs(selectedActivityEndTime).format('h:mm A') : null;
            } else if (timeMode === 'multi-day') {
                if (i === 0) {
                    isAllDay = false;
                    begin_time = hasValidBegin ? dayjs(selectedActivityBeginTime).format('h:mm A') : null;
                } else if (i === activityDaysCount - 1) {
                    isAllDay = false;
                    end_time = hasValidEnd ? dayjs(selectedActivityEndTime).format('h:mm A') : null;
                } else {
                    isAllDay = true;
                }
            }

            newSelectedDays.push({
                [`day ${i + 1}`]: dayName,
                'all-day': isAllDay,
                'begin_time': begin_time,
                'end_time': end_time,
                'notes': "",
            });
        }

        setSelectedActivityDays(newSelectedDays);
        setActivityStartingOnDay(newStartingDay);
    }

    const [timeMode, setTimeMode] = useState(
        eventActivity['time_table'].dates[0]?.begin_time || eventActivity['time_table'].days[0]?.begin_time ?
            "multi-day" : eventActivity['time_table'].dates[0]?.['all-day'] || eventActivity['time_table'].days[0]?.['all-day'] ?
                "all-day" : "multi-day"
    );

    useEffect(() => {
        setSelectTimeToggle(timeMode !== 'all-day');

        setSelectedActivityDates(prev => {
            if (timeMode === 'all-day') {
                return prev.map(entry => ({ ...entry, "all-day": true, begin_time: null, end_time: null }));
            }

            if (timeMode === 'multi-day' && prev.length > 1) {
                const sorted = [...prev].sort((a, b) => dayjs(a.date).isBefore(dayjs(b.date)) ? -1 : 1);
                return sorted.map((entry, index) => {
                    if (index === 0) return { ...entry, "all-day": false, begin_time: selectedActivityBeginTime?.format('h:mm A') || null, end_time: null };
                    if (index === sorted.length - 1) return { ...entry, "all-day": false, begin_time: null, end_time: selectedActivityEndTime?.format('h:mm A') || null };
                    return { ...entry, "all-day": true, begin_time: null, end_time: null };
                });
            }

            return prev.map(entry => ({
                ...entry,
                "all-day": false,
                begin_time: selectedActivityBeginTime?.format('h:mm A') || entry.beginTime,
                end_time: selectedActivityEndTime?.format('h:mm A') || entry.endTime,
            }));
        });
    }, [timeMode]);

    function saveEventActivitySettings() {

        const updatedActivity = {
            ...eventActivity,
            time_table: {
                ...eventActivity.time_table,
                dates: selectedActivityDates
            }
        };
        setEventActivity(updatedActivity);

        onClose();
    }

    const initialDate = useMemo(() => {
        const timeTableDate = eventActivity?.['time_table']?.dates?.[0]?.date;
        if (timeTableDate && dayjs(timeTableDate).isValid()) return dayjs(timeTableDate, "YYYY-MM-DD");
        const planningDate = PA_GlobalObject?.Planning?.['time_table']?.dates?.[0]?.date;
        if (planningDate && dayjs(planningDate).isValid()) return dayjs(planningDate, "YYYY-MM-DD");
        return dayjs();
    }, [eventActivity, PA_GlobalObject]);

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    minWidth: 350,
                    maxWidth: 500,
                    width: '100%',
                    borderRadius: '24px',
                    background: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(25px)',
                    border: '1px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    overflowX: 'hidden'
                },
            }}
        >
            <DialogTitle
                sx={{
                    color: '#1e293b',
                    textAlign: 'center',
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    pt: 3,
                    pb: 1
                }}
            >
                Activity Timeline Setup
            </DialogTitle>

            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{ position: 'absolute', right: 16, top: 16, color: '#64748b' }}
            >
                <i className="icon-cross" style={{ fontSize: '16px' }} />
            </IconButton>

            <DialogContent sx={{ pb: 1, overflowX: 'hidden' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                    <TabContext value={tabValueSchedule}>
                        <Box sx={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', width: '100%' }}>
                            <TabList
                                sx={{ background: 'rgba(255, 255, 255, 0.6)', '& .MuiTabs-indicator': { backgroundColor: '#f09637' } }}
                                centered
                                variant="fullWidth"
                                onChange={handleTabValueScheduleChange}
                                aria-label="Activity Date Setup Mode"
                            >
                                <Tab label="Calendar Date" value="date" sx={{ fontSize: '12px', fontWeight: 700, '&.Mui-selected': { color: '#f09637' } }} />
                                <Tab label="Relative Roadmap Day" value="day" sx={{ fontSize: '12px', fontWeight: 700, '&.Mui-selected': { color: '#f09637' } }} />
                            </TabList>
                        </Box>

                        <TabPanel value="date" sx={{ p: 0, display: 'flex', justifyContent: 'center', width: '100%', overflowX: 'hidden' }}>
                            <DateCalendar
                                sx={{ width: '100%', height: 'auto' }}
                                value={calendarDate}
                                onChange={(newValue) => setCalendarDate(newValue)}
                                defaultValue={initialDate}
                                referenceDate={initialDate}
                                renderLoading={() => <DayCalendarSkeleton />}
                                showDaysOutsideCurrentMonth
                                slots={{ day: ServerDay }}
                                slotProps={{
                                    day: { planningData: usePA_GlobalStore.getState().PA_GlobalObject.Planning },
                                }}
                            />
                        </TabPanel>

                        <TabPanel value="day" sx={{ p: 1, width: '100%' }}>
                            {eventHasDays ? (
                                <Box sx={{ mt: 1, width: '100%' }}>{renderActivityDaysLayoutForBegin()}</Box>
                            ) : (
                                <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1, width: '100%' }}>
                                    <TextField
                                        fullWidth
                                        label="Duration (Number of Days)"
                                        value={activityDaysCount}
                                        onChange={handleActivityDaysCountChange}
                                        type="number"
                                        InputProps={{ sx: { borderRadius: '10px' } }}
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel>Starting On Day</InputLabel>
                                        <Select
                                            value={activityStartingOnDay}
                                            label="Starting On Day"
                                            onChange={handleActivityStartingOnChange}
                                            sx={{ borderRadius: '10px' }}
                                        >
                                            <MenuItem value="">Not Selected</MenuItem>
                                            {daysOfWeek.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </TabPanel>
                    </TabContext>

                    <ToggleButtonGroup
                        value={timeMode}
                        exclusive
                        onChange={(e, newMode) => { if (newMode !== null) setTimeMode(newMode); }}
                        fullWidth
                        sx={{
                            borderRadius: '12px',
                            background: 'rgba(0,0,0,0.02)',
                            p: '4px',
                            border: '1px solid rgba(0,0,0,0.04)',
                            '& .MuiToggleButtonGroup-grouped': {
                                border: 'none',
                                borderRadius: '8px !important',
                                mx: '2px'
                            }
                        }}
                    >
                        {[
                            ...(((tabValueSchedule === 'date' && selectedActivityDates.length === 1) || (tabValueSchedule === 'day' && selectedActivityDays.length === 1)) ? [{ value: "all-day", label: "All Day" }] : []),
                            { value: "multi-day", label: "Specific Windows" }
                        ].map((option) => (
                            <ToggleButton
                                key={option.value}
                                value={option.value}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    py: 1,
                                    color: '#64748b',
                                    '&.Mui-selected': {
                                        backgroundColor: '#ffffff',
                                        color: '#f09637',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                        '&:hover': { backgroundColor: '#ffffff' }
                                    },
                                }}
                            >
                                {option.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    {selectTimeToggle && (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth>
                                <TimePicker
                                    label="Begin Time"
                                    value={dayjs(selectedActivityBeginTime, 'h:mm A')}
                                    onChange={(newTime) => setSelectedActivityBeginTime(newTime)}
                                    onAccept={(finalTime) => handleBeginTimeChange(finalTime)}
                                    ampmInClock
                                    minutesStep={1}
                                    slotProps={{
                                        textField: { size: 'small', fullWidth: true, InputProps: { sx: { borderRadius: '10px' } } },
                                        actionBar: { actions: ['accept', 'cancel'] }
                                    }}
                                />
                            </FormControl>

                            <FormControl fullWidth>
                                <TimePicker
                                    label="End Time"
                                    value={dayjs(selectedActivityEndTime, 'h:mm A')}
                                    onChange={(newTime) => setSelectedActivityEndTime(newTime)}
                                    onAccept={(finalTime) => handleEndTimeChange(finalTime)}
                                    ampmInClock
                                    minutesStep={1}
                                    slotProps={{
                                        textField: { size: 'small', fullWidth: true, InputProps: { sx: { borderRadius: '10px' } } },
                                        actionBar: { actions: ['accept', 'cancel'] }
                                    }}
                                />
                            </FormControl>
                        </Box>
                    )}

                    <Box
                        sx={{
                            background: 'rgba(255, 255, 255, 0.45)',
                            backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255, 255, 255, 0.6)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
                            borderRadius: '16px',
                            p: 2,
                            maxHeight: '160px',
                            overflowY: 'auto',
                        }}
                    >
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            Schedule Summary
                        </Typography>

                        <Stack spacing={0.5} sx={{ width: '100%' }}>
                            {tabValueSchedule === "date" && selectedActivityDates.length > 0 ? (
                                selectedActivityDates
                                    .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
                                    .map((entry, index) => {
                                        const formattedDate = dayjs(entry.date).format("ddd, MMM D");
                                        let timeInfo = entry['all-day'] ? "All Day" : (entry.begin_time && entry.end_time ? `${entry.begin_time} - ${entry.end_time}` : entry.begin_time ? `Starts at ${entry.begin_time}` : entry.end_time ? `Ends at ${entry.end_time}` : "No time set");
                                        return (
                                            <Box key={index} display="flex" justifyContent="space-between" sx={{ py: 0.5, borderBottom: '1px dashed rgba(0,0,0,0.05)' }}>
                                                <Typography sx={{ fontSize: '12px', color: '#475569' }}>{formattedDate}</Typography>
                                                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#f09637' }}>{timeInfo}</Typography>
                                            </Box>
                                        );
                                    })
                            ) : tabValueSchedule === "day" && selectedActivityDays.length > 0 ? (
                                selectedActivityDays.map((entry, index) => {
                                    let timeInfo = entry['all-day'] ? "All Day" : (entry.begin_time && entry.end_time ? `${entry.begin_time} - ${entry.end_time}` : entry.begin_time ? `Starts at ${entry.begin_time}` : entry.end_time ? `Ends at ${entry.end_time}` : "No time set");
                                    const dayKey = Object.keys(entry).find(k => k.startsWith("day "));
                                    const dayLabel = dayKey ? `${entry[dayKey].slice(0, 3)}, ${dayKey}` : "Unknown Day";
                                    return (
                                        <Box key={index} display="flex" justifyContent="space-between" sx={{ py: 0.5, borderBottom: '1px dashed rgba(0,0,0,0.05)' }}>
                                            <Typography sx={{ fontSize: '12px', color: '#475569' }}>{dayLabel}</Typography>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#f09637' }}>{timeInfo}</Typography>
                                        </Box>
                                    );
                                })
                            ) : (
                                <Typography sx={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', py: 1 }}>No dates or event tracks selected yet.</Typography>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: '16px 24px 24px 24px' }}>
                <Button onClick={onClose} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
                <Button
                    onClick={saveEventActivitySettings}
                    variant="contained"
                    sx={{
                        background: 'linear-gradient(135deg, #f09637 0%, #f2a654 100%)',
                        boxShadow: '0 4px 14px rgba(240, 150, 55, 0.3)',
                        borderRadius: '10px',
                        fontWeight: 700,
                        px: 3,
                        '&:hover': { background: 'linear-gradient(135deg, #e08526 0%, #f09637 100%)' }
                    }}
                >
                    Save Timeline
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ActivityDialogEditDates;